import React, { useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { resolveMediaUrl } from "@/components/FileUpload";
import { createMusicFader } from "@/lib/audioFade";
import { usePageHidden } from "@/lib/reelCover";
import { SoundIndicator } from "@/components/SoundIndicator";
import { Volume2 } from "lucide-react";

// Story-style background-music overlay for an IMAGE post. Autoplays (muted by
// default, like feed videos) when the images scroll into view; tapping "Sans son"
// unmutes globally. Fades in on enter, fades out on leave. Positioned over the
// parent (which must be `relative`) via `containerRef` for visibility detection.
// Lot 647 — app en arrière-plan / écran verrouillé / onglet caché → la musique se coupe net (fil ET profil)
// et reprend seulement si l'image est encore à l'écran au retour.
export const PostImageMusic = ({ containerRef, url, title, volume = 1, start = 0, testId = "post-image-music" }) => {
  const { videoMuted, setVideoMuted, t } = useApp();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const hidden = usePageHidden();
  const hiddenRef = useRef(hidden);
  const inViewRef = useRef(false);
  const faderRef = useRef(null);
  const startAtRef = useRef(start || 0);

  useEffect(() => {
    const a = audioRef.current;
    const c = containerRef?.current;
    if (!a || !c || !url) return undefined;
    a.src = resolveMediaUrl(url);
    a.loop = !start;
    a.muted = videoMuted;
    a.volume = 0;
    const startAt = start || 0;
    startAtRef.current = startAt;
    const fader = createMusicFader(a, {});
    faderRef.current = fader;
    fader.setTarget(volume);
    fader.setStart(startAt);
    fader.setGate(0);
    const onEnded = () => { if (startAt) { try { a.currentTime = startAt; a.play().catch(() => {}); } catch { /* noop */ } } };
    a.addEventListener("ended", onEnded);
    let pauseT = null;
    const io = new IntersectionObserver(([e]) => {
      inViewRef.current = e.isIntersecting;
      if (e.isIntersecting && !hiddenRef.current) {
        if (pauseT) { clearTimeout(pauseT); pauseT = null; }
        try { a.currentTime = startAt; } catch { /* noop */ }
        a.play().then(() => { setPlaying(true); fader.fadeIn(); }).catch(() => {});
      } else {
        fader.fadeOut();
        if (pauseT) clearTimeout(pauseT);
        pauseT = setTimeout(() => { try { a.pause(); } catch { /* noop */ } }, 320);
        setPlaying(false);
      }
    }, { threshold: 0.5 });
    io.observe(c);
    return () => { io.disconnect(); if (pauseT) clearTimeout(pauseT); a.removeEventListener("ended", onEnded); fader.destroy(); faderRef.current = null; try { a.pause(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, volume, start]);

  useEffect(() => { if (audioRef.current) audioRef.current.muted = videoMuted; }, [videoMuted]);

  // Arrière-plan : pause immédiate (pas de fondu de 320 ms qui continuerait de jouer) ; retour : reprise si encore visible.
  useEffect(() => {
    hiddenRef.current = hidden;
    const a = audioRef.current;
    if (!a) return;
    if (hidden) {
      faderRef.current?.setGate(0);
      try { a.pause(); } catch { /* noop */ }
      setPlaying(false);
    } else if (inViewRef.current) {
      a.play().then(() => { setPlaying(true); faderRef.current?.fadeIn(); }).catch(() => {});
    }
  }, [hidden]);

  if (!url) return null;
  return (
    <>
      <div
        className="absolute top-3 left-3 z-20 flex items-center gap-1.5 rounded-full bg-black/55 text-white px-2.5 py-1 text-xs font-semibold pointer-events-none"
        style={{ backdropFilter: "blur(4px)", textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
        data-testid={`${testId}-badge`}
      >
        <span className={`eq ${playing && !videoMuted ? "eq-on" : ""}`} aria-hidden><i /><i /><i /><i /></span>
        <span className="max-w-[150px] truncate">{title || t("post.music", "Musique")}</span>
      </div>
      {videoMuted ? (
        <SoundIndicator
          muted
          variant="compact"
          onEnable={() => setVideoMuted(false)}
          className="absolute bottom-3 right-3 z-20"
          label={t("video.no_sound", "Sans son")}
          testId={`${testId}-sound`}
        />
      ) : (
        <button
          type="button"
          onClick={() => setVideoMuted(true)}
          className="absolute bottom-3 right-3 z-20 h-9 w-9 rounded-full grid place-items-center text-white transition-transform hover:scale-110"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          aria-label={t("video.mute", "Couper le son")}
          data-testid={`${testId}-mute`}
        >
          <Volume2 size={16} />
        </button>
      )}
      <audio ref={audioRef} className="hidden" data-testid={`${testId}-audio`} />
    </>
  );
};

export default PostImageMusic;
